import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AI_GATEWAY_BASE_URL } from '../config';

const MAX_MESSAGES = 12; // keep under the gateway's hard cap
const STORAGE_KEY = 'portfolio-assistant-transcript';
const WIDTH_KEY = 'portfolio-assistant-width';

// Panel width is configurable and user-resizable (drag the left edge).
const DEFAULT_WIDTH = 380;
const MIN_WIDTH = 320;
const MAX_WIDTH = 640;

function clampWidth(px) {
  const viewportCap = typeof window !== 'undefined' ? window.innerWidth - 32 : MAX_WIDTH;
  return Math.max(MIN_WIDTH, Math.min(px, MAX_WIDTH, viewportCap));
}

function loadWidth() {
  try {
    const raw = sessionStorage.getItem(WIDTH_KEY);
    return raw ? clampWidth(parseInt(raw, 10)) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

function loadTranscript() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(-MAX_MESSAGES) : [];
  } catch {
    return [];
  }
}

function saveTranscript(messages) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_MESSAGES)));
  } catch {
    /* private mode / storage disabled — fine, it's just a convenience */
  }
}

// Parse an SSE byte stream from the gateway, invoking onText(chunk) as text arrives.
// Resolves when the stream ends; rejects with an Error carrying the gateway error code.
async function readSse(response, onText) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';

    for (const frame of frames) {
      let event = 'message';
      let data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (event === 'done') return;
      if (event === 'error') {
        const err = new Error('stream_error');
        try {
          err.code = JSON.parse(data)?.error?.code;
        } catch {
          /* ignore */
        }
        throw err;
      }
      try {
        const payload = JSON.parse(data);
        if (payload.text) onText(payload.text);
      } catch {
        /* skip malformed frame */
      }
    }
  }
}

export default function AssistantWidget() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadTranscript);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [width, setWidth] = useState(loadWidth);

  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const panelRef = useRef(null);
  const fabRef = useRef(null);

  useEffect(() => saveTranscript(messages), [messages]);

  useEffect(() => {
    try {
      sessionStorage.setItem(WIDTH_KEY, String(width));
    } catch {
      /* storage disabled — ignore */
    }
  }, [width]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, busy]);

  // Click / tap outside the panel (and not on the toggle button) minimizes it.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (panelRef.current?.contains(e.target) || fabRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  // Keep width sane if the viewport shrinks below it.
  useEffect(() => {
    const onResize = () => setWidth((w) => clampWidth(w));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const startResize = useCallback(
    (e) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = width;
      const onMove = (ev) => setWidth(clampWidth(startWidth + (startX - ev.clientX)));
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        document.body.style.userSelect = '';
      };
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [width],
  );

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    setBusy(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const send = useCallback(
    async (event) => {
      event?.preventDefault();
      const text = draft.trim();
      if (!text || busy) return;

      stop();
      setError(null);
      setDraft('');

      const outgoing = [...messages, { role: 'user', content: text }].slice(-MAX_MESSAGES);
      setMessages([...outgoing, { role: 'assistant', content: '', pending: true }]);
      setBusy(true);

      const controller = new AbortController();
      abortRef.current = controller;

      const appendToLast = (chunk) =>
        setMessages((current) => {
          const next = current.slice();
          const last = next[next.length - 1];
          next[next.length - 1] = { ...last, content: last.content + chunk, pending: false };
          return next;
        });

      try {
        const res = await fetch(`${AI_GATEWAY_BASE_URL}/portfolio/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
          body: JSON.stringify({ messages: outgoing }),
          signal: controller.signal,
        });

        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.error?.code || `http_${res.status}`);
        }

        if (res.headers.get('content-type')?.includes('text/event-stream')) {
          await readSse(res, appendToLast);
        } else {
          const body = await res.json();
          appendToLast(body.reply || '');
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        // drop the empty pending assistant bubble, surface an error line
        setMessages((current) => current.filter((m) => !m.pending));
        setError(err.code === 'rate_limited' ? t('assistant.errorRate') : t('assistant.error'));
      } finally {
        setBusy(false);
        abortRef.current = null;
      }
    },
    [draft, busy, messages, stop, t],
  );

  const reset = useCallback(() => {
    stop();
    setMessages([]);
    setError(null);
  }, [stop]);

  return (
    <>
      <button
        type="button"
        ref={fabRef}
        className={`assistant__fab${open ? ' assistant__fab--open' : ''}`}
        aria-expanded={open}
        aria-label={open ? t('assistant.close') : t('assistant.open')}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? '✕' : '✦'}
      </button>

      {open && (
        <section
          className="assistant"
          role="dialog"
          aria-label={t('assistant.title')}
          ref={panelRef}
          style={{ width: `${width}px` }}
        >
          <div
            className="assistant__resize"
            role="separator"
            aria-orientation="vertical"
            aria-label={t('assistant.resize')}
            onPointerDown={startResize}
          />

          <header className="assistant__header">
            <span className="assistant__title">{t('assistant.title')}</span>
            {messages.length > 0 && (
              <button type="button" className="assistant__reset" onClick={reset}>
                {t('assistant.reset')}
              </button>
            )}
          </header>

          <div className="assistant__log" ref={scrollRef}>
            {messages.length === 0 && <p className="assistant__greeting">{t('assistant.greeting')}</p>}
            {messages.map((m, i) => (
              <div key={i} className={`assistant__msg assistant__msg--${m.role}`}>
                {m.content || (m.pending ? '…' : '')}
              </div>
            ))}
            {error && <p className="assistant__error">{error}</p>}
          </div>

          <form className="assistant__form" onSubmit={send}>
            <input
              type="text"
              className="assistant__input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('assistant.placeholder')}
              aria-label={t('assistant.placeholder')}
              maxLength={2000}
              disabled={busy}
            />
            <button type="submit" className="assistant__send" disabled={busy || !draft.trim()}>
              {busy ? '…' : t('assistant.send')}
            </button>
          </form>

          <p className="assistant__disclaimer">{t('assistant.disclaimer')}</p>
        </section>
      )}
    </>
  );
}
